import * as React from 'react'
import {
  Box,
  Button,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  TextAreaInput,
  InputCharLimit,
  ToastNotifications,
  Text,
} from '@lightspeed/design-system-react'
import { CustomerBadge, PartialCustomer } from '@vend/react-utilities'
import { ApiCustomer } from '@vend/types'

import { useT } from '@transifex/react'
import { StepBreadcrumbs } from '../StepBreadcrumbs'
import { STEPS } from '../types'
import {
  CustomerInfo,
  PartialCustomerGroup,
  CUSTOMER_NAME_FIELD,
} from '../../../common/types'
import { trimContactInfo } from '../../../common/customer-contact-utils'
import {
  useCustomerManager,
  useRegisterManager,
  useSaleManager,
  useVdFeatureManager,
} from '../../../../../../ng-compat/hooks'
import {
  selectSaleCustomer,
  selectSaleNote,
  setSaleNote,
} from '../../../../../../state/slices/currentSale/currentSaleSlice'
import { useAppDispatch, useAppSelector } from '../../../../../../state/hooks'
import {
  THRESHOLD_SALE_NOTE_MAX_CHAR,
  SALE_NOTE_MAX_CHAR,
} from '../../../service-modal/constants'

interface Props {
  onClose: () => void
  onStepChange: (step: STEPS) => void
  onNoteChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  newCustomer: CustomerInfo | null
  selectedCustomer: ApiCustomer | null
  group?: PartialCustomerGroup
  contactChanged: boolean
  note: string
}

const CANNOT_SAVE_CUSTOMER = 'Cannot save customer'

export const Step2: React.FC<Props> = ({
  onClose,
  onStepChange,
  onNoteChange,
  newCustomer,
  selectedCustomer,
  group,
  contactChanged,
  note,
}) => {
  const [loading, setLoading] = React.useState<boolean>(false)
  const registerManager = useRegisterManager()
  const register = registerManager.getActiveRegister()
  const isNoteVisibleToCustomer = register.print_note_on_receipt

  const saleManager = useSaleManager()
  const vdFeatureManager = useVdFeatureManager()
  const customerManager = useCustomerManager()
  const saleNote = useAppSelector(selectSaleNote)
  const saleCustomer = useAppSelector(selectSaleCustomer)
  const dispatch = useAppDispatch()
  const hasExceededNoteInputLimit = note.length > SALE_NOTE_MAX_CHAR
  const shouldShowGroup = vdFeatureManager.get('customer_groups')

  const customer = newCustomer && {
    name: `${newCustomer![CUSTOMER_NAME_FIELD.FIRSTNAME] || ''} ${
      newCustomer![CUSTOMER_NAME_FIELD.LASTNAME] || ''
    }`,
    ...newCustomer,
  }

  const handleCreateQuote = () => {
    const customerPromise = selectedCustomer
      ? handleCustomerUpdate
      : handleCustomerCreate

    customerPromise()
      .then(() => {
        if (note !== saleNote) {
          dispatch(setSaleNote(note))
        }
        saleManager.quoteSale()
        onClose()
      })
      .catch(error => {
        setLoading(false)
        if (error && error !== CANNOT_SAVE_CUSTOMER)
          ToastNotifications.negative('Failed to create quote')
      })
  }

  // Convert added Customer to ApiCustomer format
  const convertCustomerToApiCustomer = (
    customer: CustomerInfo | null,
    group: PartialCustomerGroup
  ) => {
    if (customer) {
      const result = {
        first_name: customer.firstName && customer.firstName.trim(),
        last_name: customer.lastName && customer.lastName.trim(),
        mobile: customer.mobile,
        email: customer.email,
        phone: customer.phone,
        fax: customer.fax,
        customer_group_id: group.id,
      } as ApiCustomer
      trimContactInfo(result)
      return result
    }
    return null
  }

  const handleCustomerCreate = () => {
    return new Promise<void>((resolve, reject) => {
      const customer = convertCustomerToApiCustomer(newCustomer, group!)
      if (customer) {
        setLoading(true)
        return (
          customerManager
            .saveNewCustomer(customer)
            .then(savedCustomer => {
              return saleManager.addCustomer(savedCustomer)
            })
            .then(() => {
              resolve()
            })
            // customerManager will throw the error
            // eslint-disable-next-line prefer-promise-reject-errors
            .catch(() => reject(CANNOT_SAVE_CUSTOMER))
        )
      }
    })
  }

  const handleCustomerUpdate = () => {
    return new Promise<void>((resolve, reject) => {
      setLoading(true)
      if (contactChanged) {
        trimContactInfo(selectedCustomer!)
        return (
          customerManager
            .updateCustomer(selectedCustomer!)
            .then(savedCustomer =>
              saleManager.addCustomer(savedCustomer).then(() => resolve())
            )
            // eslint-disable-next-line prefer-promise-reject-errors
            .catch(() => reject(CANNOT_SAVE_CUSTOMER))
        )
      } else if (saleCustomer && saleCustomer.id === selectedCustomer!.id) {
        // Didn't change pre-attached customer
        return resolve()
      } else {
        // Selected a customer at Step1, but didn't update contact info
        return saleManager.addCustomer(selectedCustomer!).then(() => {
          return resolve()
        })
      }
    })
  }
  const t = useT()

  const secondStepCopies = {
    title: t('Contact info for the quote', {
      _context: 'Title for detail step in quote modal.',
    }),
    description: t('Enter details to contact the customer about this quote.', {
      _context: 'Description for detail step in quote modal.',
    }),
    quoteNote: t('Quote note', {
      _context: 'Input label for detail step in quote modal.',
    }),
    staffNote: t('Staff note', {
      _context: 'Input label for detail step in quote modal.',
    }),
    noteDescriptionVisible: t(
      'Customers will be able to see this note on their quote.',
      {
        _context: 'Text for detail step in quote modal.',
      }
    ),
    noteDescriptionNotVisible: t(
      'Customers will not see this note on their quote',
      {
        _context: 'Text for detail step in quote modal.',
      }
    ),
    back: t('Back', {
      _context: 'Back button text for detail step in quote modal.',
    }),
    createQuote: t('Create quote', {
      _context: 'Button text for detail step in quote modal.',
    }),
  }

  return (
    <Modal onClose={onClose}>
      <StepBreadcrumbs step={STEPS.REVIEW_DETAILS} />
      <ModalHeader>
        {secondStepCopies.title}
        <Text tag="p" className="vd-mt2">
          {secondStepCopies.description}
        </Text>
      </ModalHeader>
      <ModalContent>
        <CustomerBadge
          customer={customer as PartialCustomer}
          apiCustomer={selectedCustomer!}
          group={shouldShowGroup ? group : undefined}
          size="small"
        />
        <div className="vd-mt6">
          <span className="vd-text-label vd-mr2">
            {isNoteVisibleToCustomer
              ? secondStepCopies.quoteNote
              : secondStepCopies.staffNote}
          </span>
          <span className="vd-text-supplementary">
            {isNoteVisibleToCustomer
              ? secondStepCopies.noteDescriptionVisible
              : secondStepCopies.noteDescriptionNotVisible}
          </span>
        </div>
        <div className="vd-mt3">
          <TextAreaInput
            defaultValue={note}
            rows={5}
            className="vd-input vd-textarea"
            data-testid="customer-modal-quote-note-input"
            onChange={onNoteChange}
            hasError={hasExceededNoteInputLimit}
          />
          <InputCharLimit
            value={note}
            limit={SALE_NOTE_MAX_CHAR}
            threshold={THRESHOLD_SALE_NOTE_MAX_CHAR}
          />
        </div>
      </ModalContent>
      <ModalActions>
        <Box className="vd-btn-group">
          <Button
            variant="supplementary"
            onClick={() =>
              onStepChange(
                newCustomer ? STEPS.ADD_NEW_CUSTOMER : STEPS.SELECT_CUSTOMER
              )
            }
            disabled={loading}
            data-cy="button-back"
          >
            {secondStepCopies.back}
          </Button>
          <Button
            variant="go"
            disabled={hasExceededNoteInputLimit}
            onClick={handleCreateQuote}
            loading={loading}
            data-cy="button-create-quote"
            data-testid="button-create-quote"
          >
            {secondStepCopies.createQuote}
          </Button>
        </Box>
      </ModalActions>
    </Modal>
  )
}

Step2.displayName = 'Step2'
